import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../../../lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap ring-offset-surface-primary transition-colors focus-visible:ring-2 focus-visible:ring-border-brand-primary focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-(--primary) text-(--text) hover:shadow-md',
        loading: 'animate-pulse bg-(--primary) text-(--text) hover:shadow-md',
        success: 'bg-surface-warning-success text-white',
        disabled: 'bg-surface-disabled text-text-disabled',
        ghost: 'bg-surface-white text-text-primary hover:shadow-md',
        outline: 'rounded-lg border border-border-divider bg-surface-white text-text-primary shadow hover:shadow-md',
        warning: 'bg-surface-warning-error text-white',
        'outline-warning':
          'rounded-lg border border-surface-warning-error bg-surface-white text-surface-warning-error shadow hover:shadow-md',
        'brand-primary': 'bg-surface-brand-primary text-white hover:shadow-md',
        'brand-outline':
          'rounded-lg border border-(--primary) bg-surface-white text-(--primary) shadow hover:shadow-md',
        'loading-brand-primary': 'animate-pulse bg-surface-brand-primary text-white hover:shadow-md',
        'brand-tertiary': 'bg-surface-brand-tertiary text-white',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
